class CartDrawerFreeShipping {
  constructor() {
    this.notification = null;
    this.message = null;
    this.progressFill = null;
    this.freeShippingThreshold = null;
    this.setupComplete = false;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Re-query DOM elements in case they were replaced
    this.requeryElements();

    if (!this.notification) {
      // Retry after a short delay if element not found
      setTimeout(() => this.setup(), 100);
      return;
    }

    // Get threshold from data attribute
    const thresholdAttr = this.notification.getAttribute('data-free-shipping-threshold');
    this.freeShippingThreshold = thresholdAttr ? parseInt(thresholdAttr, 10) : 5900;

    // Only set up event listeners once
    if (!this.setupComplete) {
      // Listen to cart update events from MinimogEvents
      if (window.MinimogEvents && window.MinimogTheme) {
        this.cartUpdateUnsubscriber = window.MinimogEvents.subscribe(
          window.MinimogTheme.pubSubEvents.cartUpdate,
          (event) => {
            if (event?.cart) {
              // Re-query elements in case DOM was replaced
              // Use a small delay to ensure DOM updates are complete
              setTimeout(() => {
                this.requeryElements();
                this.updateNotification(event.cart);
              }, 50);
            }
          }
        );
      }

      // Listen to cart updated events
      this.cartUpdatedHandler = (event) => {
        if (event.detail?.cart) {
          // Re-query elements in case DOM was replaced
          // Use a small delay to ensure DOM updates are complete
          setTimeout(() => {
            this.requeryElements();
            this.updateNotification(event.detail.cart);
          }, 50);
        }
      };
      document.addEventListener('cart:updated', this.cartUpdatedHandler);

      // Listen to cart refresh events
      this.cartRefreshHandler = () => {
        // Re-query elements after DOM refresh
        setTimeout(() => {
          this.requeryElements();
          this.fetchAndUpdate();
        }, 150);
      };
      document.addEventListener('cart:refresh', this.cartRefreshHandler);

      // Listen to cart drawer open events - update free shipping when drawer opens
      if (window.MinimogEvents && window.MinimogTheme) {
        this.cartDrawerOpenUnsubscriber = window.MinimogEvents.subscribe(
          window.MinimogTheme.pubSubEvents.openCartDrawer,
          () => {
            // When cart drawer opens, update free shipping notification
            // Use a small delay to ensure DOM is ready
            setTimeout(() => {
              this.requeryElements();
              this.fetchAndUpdate();
            }, 100);
          }
        );
      }

      this.setupComplete = true;
    }

    // Initial update
    this.fetchAndUpdate();
  }

  requeryElements() {
    // Re-query all DOM elements in case they were replaced
    this.notification = document.querySelector('[data-cart-drawer-free-shipping-notification]');
    if (this.notification) {
      this.message = this.notification.querySelector('[data-cart-drawer-free-shipping-message]');
      this.progressFill = this.notification.querySelector('[data-cart-drawer-free-shipping-progress-fill]');
    }
  }

  async fetchAndUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      // Re-query elements before updating in case DOM changed
      this.requeryElements();
      this.updateNotification(cart);
    } catch (error) {
      console.error('Error fetching cart for free shipping notification:', error);
    }
  }

  updateNotification(cart) {
    // Ensure we have the notification element
    if (!this.notification) {
      this.requeryElements();
    }

    if (!this.notification || !cart) {
      return;
    }

    const currentTotal = cart.items_subtotal_price || cart.total_price;
    const currentTotalCents = parseInt(currentTotal) || 0;
    const isAchieved = currentTotalCents >= this.freeShippingThreshold;

    // Always show notification even if cart is empty (it will show full amount needed)
    this.showNotification(currentTotalCents, isAchieved);
  }

  showNotification(currentTotalCents, isAchieved) {
    // Re-query elements to ensure they exist
    if (!this.notification) {
      this.requeryElements();
    }

    if (!this.notification) {
      return;
    }

    // Re-query message and progressFill in case they were replaced
    if (!this.message || !this.progressFill) {
      this.message = this.notification.querySelector('[data-cart-drawer-free-shipping-message]');
      this.progressFill = this.notification.querySelector('[data-cart-drawer-free-shipping-progress-fill]');
    }

    const amountNeeded = this.freeShippingThreshold - currentTotalCents;
    const progress = Math.min((currentTotalCents / this.freeShippingThreshold) * 100, 100);

    // Always show notification (it will update based on cart state)
    this.notification.classList.add('m-cart-drawer__free-shipping-notification--active');
    if (isAchieved) {
      this.notification.classList.add('m-cart-drawer__free-shipping-notification--achieved');
    } else {
      this.notification.classList.remove('m-cart-drawer__free-shipping-notification--achieved');
    }

    // Update message
    if (this.message) {
      if (isAchieved) {
        this.message.innerHTML = `Congratulations! You've unlocked <strong>FREE Shipping</strong>!`;
      } else {
        const amountFormatted = this.formatMoney(Math.max(0, amountNeeded));
        this.message.innerHTML = `Buy <strong>${amountFormatted}</strong> more to enjoy <strong>FREE Shipping</strong>`;
      }
    }

    // Update progress bar
    if (this.progressFill) {
      // Force reflow to ensure transition works
      this.progressFill.style.width = '0%';
      // Use requestAnimationFrame to ensure DOM update before setting new width
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.progressFill.style.width = `${progress}%`;
        });
      });
    }
  }

  formatMoney(cents) {
    // Use global formatMoney function if available (from theme-global.js)
    if (typeof window.formatMoney === 'function') {
      const moneyFormat = window.MinimogSettings?.money_format || '{{amount}}';
      return window.formatMoney(cents, moneyFormat);
    }
    
    // Fallback if formatMoney is not available
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.MinimogSettings?.money_format || '{{amount}}');
    }
    
    // Last resort fallback
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.MinimogSettings?.currency_code || 'USD',
    }).format(cents / 100);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerFreeShipping();
  });
} else {
  new CartDrawerFreeShipping();
}

